<template>
  <view
    v-if="visible"
    class="ann-modal-mask"
    @touchmove.stop.prevent
  >
    <view class="ann-modal" @tap.stop>
      <text class="ann-modal-heading">{{ headingText }}</text>

      <view class="ann-modal-divider" />

      <scroll-view scroll-y class="ann-modal-scroll" :show-scrollbar="false">
        <view class="ann-modal-scroll-inner">
          <text v-if="titleText" class="ann-modal-title">{{ titleText }}</text>
          <view class="ann-modal-copy ann-modal-body">{{ bodyText }}</view>
          <!-- 落款/日期与正文同字体，一起滚动 -->
          <view v-if="footerAuthor || footerSignedOn" class="ann-modal-footer">
            <view v-if="footerAuthor" class="ann-modal-copy ann-modal-footer-line">{{ footerAuthor }}</view>
            <view v-if="footerSignedOn" class="ann-modal-copy ann-modal-footer-line">{{ footerSignedOn }}</view>
          </view>
        </view>
      </scroll-view>

      <view class="ann-modal-cta" @tap="onDismiss">
        <text class="ann-modal-cta-text">{{ t('home.announcement.gotIt') }}</text>
      </view>
    </view>
  </view>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useAnnouncementsStore } from '@/store/announcements';
import { useWalletStore } from '@/store/wallet';
import { resolveAnnouncementCopy } from '@/common/utils/announcementLocale';

const { t, locale } = useI18n();
const store = useAnnouncementsStore();
const walletStore = useWalletStore();
const { active: item, bannerVisible } = storeToRefs(store);

/** 仅已登录且未读公告才展示 */
const visible = computed(
  () =>
    walletStore.isAuthenticated &&
    bannerVisible.value &&
    !!item.value?.id
);

/** 弹窗顶栏固定文案「公告」：优先 heading，缺省回退 title */
const headingText = computed(() => {
  const heading = String(t('home.announcement.heading') || '').trim();
  if (heading && heading !== 'home.announcement.heading') return heading;
  return t('home.announcement.title');
});

const localized = computed(() =>
  resolveAnnouncementCopy(item.value, String(locale.value || 'en'))
);

const titleText = computed(() => localized.value.title);

/** 正文不 trim，保留接口原样给 pre-wrap */
const bodyText = computed(() => localized.value.body);

const footerAuthor = computed(() => localized.value.author);
const footerSignedOn = computed(() => localized.value.signedOnLabel);

const onDismiss = () => {
  store.dismissBanner();
};
</script>

<style lang="scss" scoped>
.ann-modal-mask {
  position: fixed;
  inset: 0;
  z-index: 20000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48rpx 56rpx;
  box-sizing: border-box;
}

.ann-modal {
  position: relative;
  width: 100%;
  max-width: 620rpx;
  background: #ffffff;
  border-radius: 40rpx;
  padding: 40rpx 20rpx 40rpx;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.ann-modal-heading {
  display: block;
  text-align: center;
  font-size: 38rpx;
  font-weight: 700;
  color: #000000;
  line-height: 1.3;
  margin-bottom: 24rpx;
}

.ann-modal-divider {
  width: 100%;
  height: 1px;
  background: #e8e8e8;
  margin-bottom: 24rpx;
}

.ann-modal-scroll {
  width: 100%;
  max-height: 440rpx;
  margin-bottom: 28rpx;
}

.ann-modal-scroll-inner {
  width: 100%;
  padding-bottom: 8rpx;
  box-sizing: border-box;
}

.ann-modal-title {
  display: block;
  text-align: left;
  font-size: 32rpx;
  font-weight: 500;
  color: #171b24;
  line-height: 1.4;
  margin-bottom: 16rpx;
}

/* 正文 / 发稿人 / 发稿日共用同一套字体 */
.ann-modal-copy {
  display: block;
  width: 100%;
  font-size: 26rpx;
  font-weight: 400;
  color: #666666;
  line-height: 1.75;
  font-family: inherit;
  box-sizing: border-box;
}

.ann-modal-body {
  text-align: left;
  white-space: pre-wrap;
  word-break: break-word;
}

.ann-modal-footer {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  width: 100%;
  margin-top: 28rpx;
  padding: 0 0 4rpx;
  gap: 6rpx;
  box-sizing: border-box;
}

.ann-modal-footer-line {
  text-align: right;
}

.ann-modal-cta {
  align-self: center;
  min-width: 280rpx;
  height: 72rpx;
  padding: 0 48rpx;
  border-radius: 36rpx;
  background: #171b24;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex-shrink: 0;
}

.ann-modal-cta-text {
  font-size: 28rpx;
  font-weight: 600;
  color: #ffffff;
}
</style>
